import { type WalletClient, type TransactionRequest, encodeFunctionData } from "viem";
import { type TransactionHash, TOKEN_IDS } from "@/types";
import { gatewayV1Abi } from "@/abi";
import { getGatewayContractAddress } from "@/config";
import { estimateContractTransactionGas, writeContractTransaction } from "@/utils";
import { prepareFormTokenAmountDecimals } from "@/utils/token";

/**
 * Prepares a mint and stake transaction
 */
export const prepareMintAndStakeTransaction = async (
    amount: number,
    walletClient: WalletClient,
): Promise<TransactionRequest> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    if (!walletClient.chain?.id) {
        throw new Error("Chain ID is not available");
    }

    const amountBigInt = prepareFormTokenAmountDecimals(amount, TOKEN_IDS.USDC);
    const contractAddress = getGatewayContractAddress(walletClient.chain.id);

    return {
        to: contractAddress,
        data: encodeFunctionData({
            abi: gatewayV1Abi,
            functionName: "mintAndStake",
            args: [walletClient.account.address, amountBigInt],
        }),
    };
};

/**
 * Executes a mint and stake transaction
 */
export const gatewayMintAndStakeTransaction = async (
    amount: number,
    walletClient: WalletClient,
): Promise<TransactionHash> => {
    const txRequest = await prepareMintAndStakeTransaction(amount, walletClient);
    const hash = await writeContractTransaction(walletClient, txRequest);
    return hash as TransactionHash;
};

/**
 * Estimates gas for a mint and stake transaction
 */
export const estimateMintAndStakeTransactionGas = async (
    amount: number,
    walletClient: WalletClient,
): Promise<bigint> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareMintAndStakeTransaction(amount, walletClient);
    return estimateContractTransactionGas(walletClient, txRequest);
};
